import { MoreHorizontal, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAgentChat } from '@/components/agent-chat/context/AgentChatContext';
import { CreateThreadDialog } from '@/components/agent-chat/threads/CreateThreadDialog';
import { ThreadTab } from '@/components/agent-chat/threads/ThreadTab';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
} from '@/components/ui/catalyst/dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownMenuWithoutGrid,
} from '@/components/ui/catalyst/dropdown';
import { Text } from '@/components/ui/catalyst/text';
import {
  useCreateAgentChatThreadMutation,
  useDeleteAgentChatThreadMutation,
  useListAgentChatThreadsQuery,
  useUpdateAgentChatThreadMutation,
} from '@/services/api/agentChatService';

interface ChatHeaderControlsProps {
  matterId: string;
  workspaceId?: string;
}

// Number of threads to display in the active tray before overflowing into the dropdown
const ACTIVE_THREAD_LIMIT = 5;

export const ChatHeaderControls = ({
  matterId,
  workspaceId,
}: ChatHeaderControlsProps) => {
  const { selectedThreadId, setSelectedThreadId } = useAgentChat();

  // Fetch threads
  const { data: threads = [], isLoading } = useListAgentChatThreadsQuery({
    matterId,
    workspaceId,
  });

  // Mutations
  const [createThread] = useCreateAgentChatThreadMutation();
  const [renameThread] = useUpdateAgentChatThreadMutation();
  const [deleteThread] = useDeleteAgentChatThreadMutation();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null);
  const [openedThreadIds, setOpenedThreadIds] = useState<string[]>([]);

  // On initial load open most recent thread
  useEffect(() => {
    if (!threads.length || openedThreadIds.length) return;

    const mostRecent = [...threads].sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    )[0];
    if (mostRecent) {
      setOpenedThreadIds([mostRecent.id]);
      if (!selectedThreadId) setSelectedThreadId(mostRecent.id);
    }
  }, [threads, openedThreadIds.length, selectedThreadId, setSelectedThreadId]);

  // Remove stale ids
  useEffect(() => {
    const ids = new Set(threads.map((t) => t.id));
    setOpenedThreadIds((prev) => prev.filter((id) => ids.has(id)));
  }, [threads]);

  // Active/inactive split
  const { activeThreads, inactiveThreads } = useMemo(() => {
    if (!threads.length) return { activeThreads: [], inactiveThreads: [] };
    const active = openedThreadIds
      .map((id) => threads.find((t) => t.id === id))
      .filter((t): t is (typeof threads)[number] => Boolean(t))
      .slice(0, ACTIVE_THREAD_LIMIT);
    const activeIds = new Set(active.map((t) => t.id));
    const inactive = threads.filter((t) => !activeIds.has(t.id));
    return { activeThreads: active, inactiveThreads: inactive };
  }, [threads, openedThreadIds]);

  const handleSelectThread = useCallback(
    (id: string | null) => {
      setSelectedThreadId(id);
      if (id)
        setOpenedThreadIds((prev) =>
          prev.includes(id) ? prev : [...prev, id],
        );
    },
    [setSelectedThreadId],
  );

  const handleCreateThread = async (
    title: string,
    initialTask: string | null,
  ) => {
    const thread = await createThread({
      matterId,
      data: { title, initial_task: initialTask, workspace_id: workspaceId },
    }).unwrap();
    setSelectedThreadId(thread.id);
    setOpenedThreadIds((prev) => [...prev, thread.id]);
  };

  const handleRenameThread = async (threadId: string, newTitle: string) => {
    await renameThread({
      matterId,
      threadId,
      data: { title: newTitle },
    }).unwrap();
  };

  const closeFromTray = (id: string) => {
    setOpenedThreadIds((prev) => {
      const next = prev.filter((tid) => tid !== id);
      if (id === selectedThreadId) setSelectedThreadId(next[0] ?? null);
      return next;
    });
  };

  const handleDeleteThread = async (threadId: string) => {
    await deleteThread({ matterId, threadId }).unwrap();
    closeFromTray(threadId);
  };

  const renderTabs = () => {
    if (isLoading) return <Text>Loading...</Text>;
    if (!threads.length) return <Text>No threads</Text>;
    return (
      <div className="flex flex-row space-x-2 overflow-x-auto pr-2">
        {activeThreads.map((thread) => (
          <ThreadTab
            key={thread.id}
            id={thread.id}
            title={thread.title}
            isSelected={selectedThreadId === thread.id}
            onSelect={handleSelectThread}
            onDelete={(id) => setThreadToDelete(id)}
            onRename={handleRenameThread}
            onRemoveFromTray={closeFromTray}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Active tray + controls */}
      <div className="flex items-center space-x-2 w-full justify-between">
        {renderTabs()}
        {/* Dropdown & create */}
        <div className="flex items-center space-x-2">
          {inactiveThreads.length > 0 && (
            <Dropdown key={selectedThreadId ?? 'no-thread'}>
              <DropdownButton
                as="button"
                className="p-1 rounded-md hover:bg-gray-100"
                aria-label="More threads"
              >
                <MoreHorizontal className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenuWithoutGrid className="max-h-60! overflow-auto p-1 space-y-1 flex! flex-col">
                {inactiveThreads.map((thread) => (
                  <ThreadTab
                    key={thread.id}
                    id={thread.id}
                    title={thread.title}
                    isSelected={selectedThreadId === thread.id}
                    onSelect={handleSelectThread}
                    onDelete={(id) => setThreadToDelete(id)}
                    onRename={handleRenameThread}
                    onRemoveFromTray={closeFromTray}
                    className="w-64"
                  />
                ))}
              </DropdownMenuWithoutGrid>
            </Dropdown>
          )}
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Delete dialog */}
      <Dialog
        open={threadToDelete !== null}
        onClose={() => setThreadToDelete(null)}
      >
        <DialogBody>
          <div className="p-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm deletion
            </h3>
            <p className="text-gray-600">
              Are you sure you want to delete this thread? This action cannot be
              undone.
            </p>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            outline
            onClick={() => setThreadToDelete(null)}
            className="mr-2"
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => threadToDelete && handleDeleteThread(threadToDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <CreateThreadDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateThread={handleCreateThread}
      />
    </>
  );
};
